import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { PaginaDeAcessos } from './acessos/PaginaDeAcessos.jsx';
import { PaginaDoConsole } from './acessos/PaginaDoConsole.jsx';
import { PaginaDaAgenda } from './agenda/PaginaDaAgenda.jsx';
// 08 · alunos. A fonte de busca global se registra por efeito colateral
// desta importação — antes de qualquer render do Shell, que a consome.
// Módulos seguintes (comanda, faturas…) seguem o mesmo padrão: um
// import ao lado deste, apontando para o próprio `buscaGlobal.ts`.
import './alunos/buscaGlobal.js';
import { PaginaDeAlunos } from './alunos/PaginaDeAlunos.jsx';
import { ErroDeApi } from './api/cliente.js';
import { Login } from './auth/Login.jsx';
import { ProvedorDeSessao, useSessao } from './auth/SessaoContext.jsx';
import { RotaProtegida } from './auth/RotaProtegida.jsx';
import { PaginaDoCaixa } from './financeiro/PaginaDoCaixa.jsx';
import { PaginaDoFinanceiro } from './financeiro/PaginaDoFinanceiro.jsx';
import { Configuracoes } from './paginas/Configuracoes.jsx';
import { EmBreve } from './paginas/EmBreve.jsx';
import { Painel } from './paginas/Painel.jsx';
import { Referencia } from './paginas/Referencia.jsx';
import { rotaInicial } from './shell/navegacao.js';
import { Shell } from './shell/Shell.jsx';
// 10 · turmas
import { PaginaDeTurmas } from './turmas/PaginaDeTurmas.jsx';
// 12 · planos
import { PaginaDePlanos } from './planos/PaginaDePlanos.jsx';
import { ProvedorDeDica } from './ui/Dica.jsx';
import { SpriteDeIcones } from './ui/Icone.jsx';
import { ProvedorDeToast } from './ui/Toast.jsx';
// 09 · importação
import { PaginaDaImportacao } from './importacao/PaginaDaImportacao.jsx';
// 14 · site
import { PaginaDoSite } from './site/PaginaDoSite.jsx';
// 20 · observabilidade
import { PaginaDeOperacao } from './operacao/PaginaDeOperacao.jsx';
// plataforma · o painel do administrador
import { PaginaDaPlataforma } from './operacao/PaginaDaPlataforma.jsx';
// 13 · estoque e comandas
import { PaginaDoBar } from './estoque/PaginaDoBar.jsx';
import { PaginaDoEstoque } from './estoque/PaginaDoEstoque.jsx';

/**
 * A raiz.
 *
 * O TanStack Query é a camada de dados por uma razão que aparece já no
 * documento 06: a agenda precisa de POLLING curto, porque o Realtime do
 * Supabase foi descartado. `refetchInterval` numa query resolve isso em
 * uma linha; um `useEffect` com `setInterval` resolveria com quatro
 * bugs de corrida.
 */
const cliente = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (tentativas, erro) => {
        // 403 e 400 não melhoram com insistência: o perfil continua
        // sem permissão e o corpo continua inválido. Repetir só atrasa
        // a mensagem que a pessoa precisa ler.
        if (erro instanceof ErroDeApi && erro.status < 500) return false;
        return tentativas < 2;
      },
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={cliente}>
      <ProvedorDeSessao>
        <ProvedorDeToast>
          <ProvedorDeDica>
            <SpriteDeIcones />
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<Login />} />

                <Route
                  element={
                    <RotaProtegida>
                      <Shell />
                    </RotaProtegida>
                  }
                >
                  <Route index element={<Entrada />} />
                  <Route path="/painel" element={<Painel />} />
                  <Route path="/agenda" element={<PaginaDaAgenda />} />
                  <Route path="/alunos" element={<PaginaDeAlunos />} />
                  {/* 10 · turmas */}
                  <Route path="/turmas" element={<PaginaDeTurmas />} />
                  {/* 13 · estoque e comandas */}
                  <Route path="/bar" element={<PaginaDoBar />} />
                  {/* 12 · planos */}
                  <Route path="/planos" element={<PaginaDePlanos />} />
                  <Route path="/financeiro" element={<PaginaDoFinanceiro />} />
                  <Route path="/caixa" element={<PaginaDoCaixa />} />
                  <Route path="/estoque" element={<PaginaDoEstoque />} />
                  <Route path="/bi" element={<EmBreve titulo="BI de ocupação" documento="17" />} />
                  <Route path="/acessos" element={<PaginaDeAcessos />} />
                  <Route path="/auditoria" element={<PaginaDoConsole />} />
                  <Route path="/configuracoes" element={<Configuracoes />} />
                  {/* 14 · site */}
                  <Route path="/site" element={<PaginaDoSite />} />

                  {/* Fora do menu: instrumento de conferência, não tela de produto. */}
                  <Route path="/referencia" element={<Referencia />} />

                  {/* 09 · importação — fora do menu: chega-se por "Importar CSV" em alunos. */}
                  <Route path="/alunos/importacoes/:id" element={<PaginaDaImportacao />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />

                {/*
                  20 · observabilidade — a tela do operador. FORA do shell e
                  da RotaProtegida: não usa sessão de arena, usa a credencial
                  de operador (ver a página). Depois do `*` sem problema: o
                  React Router 6 escolhe a rota pela especificidade, não pela
                  ordem.
                */}
                <Route path="/operacao" element={<PaginaDeOperacao />} />
                {/* plataforma — o cadastro de arenas, pela mesma credencial. */}
                <Route path="/plataforma" element={<PaginaDaPlataforma />} />
              </Routes>
            </BrowserRouter>
          </ProvedorDeDica>
        </ProvedorDeToast>
      </ProvedorDeSessao>
    </QueryClientProvider>
  );
}

/**
 * `/` leva à primeira tela que o perfil pode ver. O Professor não tem o
 * painel executivo; ele entra na agenda.
 */
function Entrada() {
  const { pode } = useSessao();
  return <Navigate to={rotaInicial(pode)} replace />;
}
